import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Intercom from '@intercom/messenger-js-sdk';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CustomThemeProvider } from './contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import 'moment/locale/fr';

import { onAuthStateChanged } from 'firebase/auth';
import { I18nextProvider } from 'react-i18next';
import { Backdrop, Modal, Paper } from '@mui/material';
import { CircularProgress } from '@material-ui/core';
import { ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearBusinessData,
  fetchBusinessData,
  setCurrentPlatform,
  setCurrentUser,
  setEmployees,
  setNotifsCount,
  setOnCall,
  setUserAuthenticationStatus,
  setWorkSessions,
} from './redux/actions-v2/coreAction';
import Login from './screens/signin/Login';
import { auth, db } from './firebase';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import PrivateRoutes from './PrivateRoutes';
import PublicRoutes from './PublicRoutes';
import ResetPassword from './screens/website/ResetPassword';
import SelectBusiness from './screens/signin/SelectBusiness.jsx';
import { fetchDataSuccess } from './redux/actions-v2/listAction.js';

import initI18n, { resetInitialized as resetI18n } from './i18n';
import initI18nWS, { resetInitialized as resetI18nWS } from './i18nWS';
import ForgotPassword from './screens/signin/ForgotPassword.jsx';
import AIRoutes from './AIRoutes.jsx';
import ConnectRoutes from './ConnectRoutes.jsx';
import InsightsRoutes from './InsightsRoutes.jsx';
import { LockOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import NodeRoutes from './NodeRoutes.jsx';
import BosRoutes from './BosRoutes.jsx';
import ChekupRoutes from './ChekupRoutes.jsx';

initI18n();

const App = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentURL = window.location.href;
  const currentPath = window.location.pathname;
  const currentDomain = window.location.hostname;
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(true);

  const currentLangCode = i18n?.language || 'fr';
  const businessFirebaseId = localStorage.getItem('businessId');
  const businessPreference = useSelector((state) => state.core.businessData);
  const currentPlatform = useSelector(state => state.core.currentPlatform);
  const mode = localStorage.getItem('mode') || 'light';
  const storedMainColor = localStorage.getItem('mainColor') || '#12B3D8';
  const storedSecColor = localStorage.getItem('secColor') || '#000000';
  const storedBusinessName = localStorage.getItem('businessName') || '';
  const businessMainColor = businessPreference?.mainColor || '#265BC2';
  const businessSecColor = businessPreference?.secColor || '#12088A';


  if (currentPlatform === 'CHEKUP') {
    initI18nWS();
  }

  const currentUser = useSelector((state) => state.core.user);
  const [planCode, setPlanCode] = useState('');
  const [isDev, setIsDev] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lockedAccountReason, setLockedAccountReason] = useState('');
  const [lockedAccountTime, setLockedAccountTime] = useState(0);
  const [openModalPlatform, setOpenModalPlatform] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        dispatch(setUserAuthenticationStatus(true));
        dispatch(setCurrentUser(user, businessFirebaseId));
        localStorage.setItem('userId', user.uid);
        const userRef = doc(db, 'users', user?.uid);
      } else {
        dispatch(setUserAuthenticationStatus(false));
        dispatch(setCurrentUser(null, ''));
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [businessPreference?.id, currentUser?.uid]);



  useEffect(() => {
    if (currentDomain?.includes('usenode')) {
      dispatch(setCurrentPlatform('NODE'));
      localStorage.setItem('platform', 'NODE');
      setOpenModalPlatform(false);
    } else if (currentDomain?.includes('bluelink')) {
      dispatch(setCurrentPlatform('NODE'));
      localStorage.setItem('platform', 'BLUELINK');
      setOpenModalPlatform(false);
    } else if (currentDomain?.includes('chekup')) {
      localStorage.setItem('mainColor', '#bc0c0c');
      localStorage.setItem('secColor', '#000000');
      dispatch(setCurrentPlatform('CHEKUP'));
      localStorage.setItem('platform', 'CHEKUP');
      setOpenModalPlatform(false);
    } else if (currentDomain?.includes('localhost')) {
      if (!currentPlatform) {
        setOpenModalPlatform(true);
      }
      if (currentPlatform) {
        setOpenModalPlatform(false);
        localStorage.setItem('platform', currentPlatform);
      }
    }
  }, [currentDomain, currentPlatform]);


  useEffect(() => {
    if (currentPlatform && businessFirebaseId && currentUser?.uid) {
      dispatch(fetchBusinessData(businessFirebaseId, t, currentLangCode, null, currentPlatform));
    }
  }, [currentPlatform]);


  const setMomentLocale = () => {
    const userLang = i18n.language || 'fr';
    if (userLang === 'fr') {
      moment.locale('fr');
    } else if (userLang === 'en') {
      moment.locale('en');
    }
  };

  useEffect(() => {
    setMomentLocale();
    if (currentPlatform === 'CHEKUP') {
      resetI18nWS();
      initI18nWS();
    } else {
      resetI18n();
      initI18n();
    }
  }, [i18n.language, currentPlatform]);




  useEffect(() => {
    if (
      currentUser?.uid &&
      businessFirebaseId &&
      currentUser?.activeBusiness?.intercomHash && !currentURL?.startsWith('http://localhost')
    ) {
      Intercom({
        app_id: 'iy97tcg7',
        user_id: currentUser?.uid,
        name: currentUser?.displayName,
        email: currentUser?.email,
        user_hash: currentUser?.activeBusiness?.intercomHash,
        Access: currentUser?.activeBusiness?.role,
        company: {
          id: businessFirebaseId || businessPreference?.id,
          name: businessPreference?.name,
          plan: businessPreference?.formula?.toUpperCase(),
        },
      });
    } else if (
      !currentPath?.startsWith('/structure-public') &&
      !currentPath?.startsWith('/doc')
    ) {
      Intercom({
        app_id: 'iy97tcg7',
      });
    }
  }, [currentUser?.uid, businessFirebaseId, businessPreference?.id]);



  useEffect(() => {
    const script = document.getElementById('pinc.helper');
    if (script && businessPreference?.id) {
      script.dataset.userId = businessPreference?.id;
    }
    setPlanCode(businessPreference?.planCode);
    setIsDev(businessPreference?.isDev);
  }, [businessPreference?.id]);

  useEffect(() => {
    let unsubscribeUpdates;

    if (businessPreference?.id && currentUser.uid) {
      const channelConfigRef = doc(
        db,
        'businessesOnNode',
        businessPreference?.id,
        'channels',
        'updates'
      );

      unsubscribeUpdates = onSnapshot(channelConfigRef, (snapshot) => {
        if (snapshot.exists) {
          const updates = snapshot?.data();

          if (updates?.lockedAccount) {
            setLocked(true);
            setLockedAccountReason(updates?.lockedAccountReason);
            setLockedAccountTime(updates?.lockedAccountEstimated);
            return;
          } else {
            setLocked(false);
            setLockedAccountReason('');
            setLockedAccountTime(0);
          }

          dispatch(setWorkSessions(updates?.nodes || []));
          dispatch(setOnCall(updates?.presences || {}));

          const maptoarray = Object.entries(updates?.employees || {})?.map(
            ([id, value]) => ({
              id,
              ...value,
            })
          );

          dispatch(
            setEmployees({
              employees: maptoarray || [],
            })
          );

          dispatch(fetchDataSuccess(maptoarray || [], 'employees'));

          //find the nodes (under updates?.nodes)
          const unseenNodes = updates?.nodes?.filter(
            (node) => !node?.seenLast?.includes(currentUser?.uid)
          );

          //remove nodes where updated_on is not today
          const today = moment().format('YYYY-MM-DD');
          const todayNodes = unseenNodes?.filter(
            (node) => node?.updated_on === today
          );

          if (todayNodes?.length > 0) {
            const idsMessages = todayNodes?.map((node) => node?.lastMessageId);
            const prevIdsMessages = JSON.parse(
              sessionStorage.getItem('unseenMessages')
            );
            if (prevIdsMessages) {
              const allIdsMessages = [...prevIdsMessages, ...idsMessages];
              sessionStorage.setItem(
                'unseenMessages',
                JSON.stringify(allIdsMessages)
              );
            } else {
              sessionStorage.setItem(
                'unseenMessages',
                JSON.stringify(idsMessages)
              );
            }
          }

          if (
            updates?.internalVersion > businessPreference?.internalVersion &&
            currentUser?.uid
          ) {
            dispatch(
              fetchBusinessData(businessPreference?.id, t, currentLangCode, null, currentPlatform)
            );
          }
        }
      });
    }
    return () => {
      if (unsubscribeUpdates) {
        unsubscribeUpdates();
      }
    };
  }, [businessPreference?.id, currentUser?.uid, currentPlatform]);



  useEffect(() => {
    if (businessFirebaseId && currentUser?.uid) {
      if (businessFirebaseId !== businessPreference?.id && currentUser?.uid) {
        dispatch(fetchBusinessData(businessFirebaseId, t, currentLangCode, null, currentPlatform));
      }
    } else {
      dispatch(clearBusinessData());
    }
  }, [businessPreference?.id, currentUser?.uid, currentPlatform]);


  useEffect(() => {
    if (
      currentURL === 'https://usenode.com/' ||
      currentURL === 'https://beta.usenode.com/' ||
      currentURL === 'http://localhost:3000/' ||
      currentURL === 'https://chekup.ca/'
    ) {
      localStorage.removeItem('businessId');

      dispatch(
        fetchBusinessData(null, t, currentLangCode, {
          mainColor: storedMainColor,
          secColor: storedSecColor,
          name: storedBusinessName,
        }, currentPlatform)
      );
    }
  }, [currentURL, currentPlatform]);

  useEffect(() => {
    return () => {
      setIsAnimationPlaying(false);
    };
  }, []);


  const setRightPlatform = (platform) => {
    if (platform === 'CHEKUP') {
      localStorage.setItem('mainColor', '#bc0c0c');
      localStorage.setItem('secColor', '#000000');
      dispatch(setCurrentPlatform('CHEKUP'))
    } else if (platform === 'NODE') {
      dispatch(setCurrentPlatform('NODE'))
    }
  }


  if (!currentPlatform) {
    return (
      <div>
        {openModalPlatform && (
          <Modal
            open={openModalPlatform}
            onClose={() => setOpenModalPlatform(false)}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Paper sx={{ width: '400px', height: '300px', padding: 5 }}>
              <div className="row middle-content">
                <div className='col-3' onClick={() => setRightPlatform('NODE')}><img src='https://storage.googleapis.com/business-logos-node/6jyRhYMJ45SObIlg89EK.png' width={60} alt='BOS' /></div>
                <div className='col-3' onClick={() => setRightPlatform('CHEKUP')}><img src='https://storage.googleapis.com/business-logos-node/chekup.png' alt='CHEKUP' width={60} /></div>
              </div>
            </Paper>
          </Modal>
        )}

      </div>
    )
  }


  return (
    <I18nextProvider i18n={i18n}>

      <CustomThemeProvider
        mode={mode}
        businessMainColor={businessMainColor}
        businessSecColor={businessSecColor}
      >
        <BrowserRouter>
          <Suspense
            fallback={
              <div>
                <div className="loader">
                  Loading...
                </div>
              </div>
            }
          >


            {planCode === 'billing' && (
              <div
                onClick={() => {
                  setPlanCode('');
                }}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  zIndex: 99999,
                  fontWeight: 400,
                  fontSize: '13px',
                  padding: '6px',
                  color: 'white',
                  textAlign: 'center',
                  top: 12,
                  left: '50%',
                  backgroundColor: 'red',
                  width: '100%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {t('billingIssuePlan')}
              </div>
            )}

            {isDev && currentPath?.startsWith('/bos') && (
              <div
                onClick={() => {
                  setIsDev(false);
                }}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  zIndex: 99999,
                  fontWeight: 400,
                  fontSize: '13px',
                  padding: '6px',
                  color: 'white',
                  textAlign: 'center',
                  top: 12,
                  left: '50%',
                  backgroundColor: 'black',
                  width: '100%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {t('devMode')}
              </div>
            )}
            <Routes>
              <Route path="/signin" element={<Login />} />
              <Route path="/select-business" element={<SelectBusiness />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/*" element={<PublicRoutes />} />
              {currentUser?.uid && (<Route path="/app/*" element={<PrivateRoutes />} />)}
              {currentUser?.uid && (<Route path="/app/chekup/*" element={<ChekupRoutes />} />)}
              {currentUser?.uid && (<Route path="/bos/*" element={<BosRoutes />} />)}
              {currentUser?.uid && (<Route path="/ai/*" element={<AIRoutes />} />)}
              {currentUser?.uid && (<Route path="/pulse/*" element={<InsightsRoutes />} />)}
              {currentUser?.uid && (<Route path="/desk/*" element={<ConnectRoutes />} />)}
              {currentUser?.uid && (<Route path="/node/*" element={<NodeRoutes />} />)}
            </Routes>
          </Suspense>
        </BrowserRouter>

        <ToastContainer
          position="bottom-right"
          autoClose={4000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={mode === 'dark' ? 'dark' : 'light'}
        />
      </CustomThemeProvider>

    </I18nextProvider>
  );
};

export default App;
